/**
 * Session-key types for LX conversation contexts.
 *
 * ## Format (aligned with the Lexiun plugin)
 *
 * Mirrors `lexruntime/openclaw/extensions/lexiun/src/session-keys.ts` — the
 * plugin-side parser is the single source of truth. **Task D (Flat Case Storage,
 * 2026-07-10):** the `case` scope is now **caseId-only** (`agent:main:lx:case:{caseId}`)
 * — a flat case resolves its folder + transcript by caseId, and its parents are
 * pure (possibly-null) metadata. The plugin's `parseLxSessionKey` accepts BOTH the
 * caseId-only `case:{caseId}` and the legacy 4-level `client:{c}:matter:{m}:case:{id}`
 * (so un-migrated transcripts still resolve). This flip is LOCKSTEP with the runtime
 * transcript migration (`migrate-conversation-keys.ts`) — see
 * `ai-docs/handoffs/flat-case-caseid-session-key-flip-lockstep-relay-2026-07-10.md`.
 *
 *   global   → `agent:main:lx:global`
 *   client   → `agent:main:lx:client:{c}`
 *   matter   → `agent:main:lx:client:{c}:matter:{m}`
 *   case     → `agent:main:lx:case:{caseId}`                         (Task D — caseId-only)
 *   processo → `agent:main:lx:client:{c}:matter:{m}:case:{caseId}:proc:{processoId}`  (still 4-level)
 *   petition → `…:case:{caseId}:pet:{petitionId}`                    (still 4-level)
 *   document → `…:case:{caseId}:doc:{docPath}`                       (still 4-level)
 *   research → `agent:main:lx:research:{topicId}`  (org-scoped, no chain)
 *
 * The proc/pet/doc leaves KEEP the 4-level chain (runtime handback decision — they
 * are unshipped; a caseId-only sub-leaf is rejected by both parsers).
 *
 * ## No fallbacks
 *
 * `buildLxSessionKey` throws `InvalidSessionContextError` when required ids for the
 * requested type are missing (the `case` scope now needs only `caseId`).
 * `parseLxSessionKey` returns `null` for any shape it doesn't recognize. Callers
 * that don't have the id yet fall back to the `global` scope key until it's available.
 *
 * ## Drift-risk warning
 *
 * This file duplicates `lexruntime/openclaw/extensions/lexiun/src/session-keys.ts`
 * line-for-line because lexide can't import plugin code directly. **If
 * the plugin's format ever changes, update this file in lockstep** —
 * any asymmetry silently breaks context injection. A future pass should
 * extract both into a shared `@lexiun/session-keys` workspace package.
 */

export type LxContextType =
  | "global"
  | "client"
  | "matter"
  | "case"
  | "processo"
  | "petition"
  | "research"
  | "document";

export interface LxSessionContext {
  type: LxContextType;
  clientId?: string;
  matterId?: string;
  caseId?: string;
  /** When type="document", the zone-relative path of the file
   *  (e.g. `07_drafts/contestacao.md`). Canonical — parse also
   *  returns this field. */
  docPath?: string;
  processoId?: string;
  petitionId?: string;
  topicId?: string;
  /** @deprecated Pre-L.6.1 legacy field. Use `docPath` for AIEditor /
   *  WorkspaceFilePane documents. Retained on the build side so
   *  callers that still pass `documentId` don't silently break, but
   *  `parseLxSessionKey` no longer emits it. */
  documentId?: string;
}

const LX_PREFIX = "agent:main:lx:";

export class InvalidSessionContextError extends Error {
  constructor(
    public readonly contextType: LxContextType,
    public readonly missingField: string,
  ) {
    super(
      `LX session context (type="${contextType}"): required field "${missingField}" is missing.`,
    );
  }
}

function requireField<T>(
  value: T | undefined,
  contextType: LxContextType,
  field: string,
): T {
  if (value === undefined || value === null || value === "") {
    throw new InvalidSessionContextError(contextType, field);
  }
  return value;
}

export function buildLxSessionKey(ctx: LxSessionContext): string {
  switch (ctx.type) {
    case "global":
      return `${LX_PREFIX}global`;

    case "research": {
      const topicId = requireField(ctx.topicId, "research", "topicId");
      return `${LX_PREFIX}research:${topicId}`;
    }

    case "client": {
      const clientId = requireField(ctx.clientId, "client", "clientId");
      return `${LX_PREFIX}client:${clientId}`;
    }

    case "matter": {
      const clientId = requireField(ctx.clientId, "matter", "clientId");
      const matterId = requireField(ctx.matterId, "matter", "matterId");
      return `${LX_PREFIX}client:${clientId}:matter:${matterId}`;
    }

    case "case": {
      // Flat Case Storage (Task D, 2026-07-10): the case scope is caseId-ONLY.
      // The runtime resolves the flat folder + transcript by caseId; client/matter
      // are pure metadata (an unassigned case has none). The proc/pet/doc leaves
      // stay 4-level per the runtime handback — do NOT flip them.
      const caseId = requireField(ctx.caseId, "case", "caseId");
      return `${LX_PREFIX}case:${caseId}`;
    }

    case "processo": {
      const clientId = requireField(ctx.clientId, "processo", "clientId");
      const matterId = requireField(ctx.matterId, "processo", "matterId");
      const caseId = requireField(ctx.caseId, "processo", "caseId");
      const processoId = requireField(
        ctx.processoId,
        "processo",
        "processoId",
      );
      return `${LX_PREFIX}client:${clientId}:matter:${matterId}:case:${caseId}:proc:${processoId}`;
    }

    case "petition": {
      const clientId = requireField(ctx.clientId, "petition", "clientId");
      const matterId = requireField(ctx.matterId, "petition", "matterId");
      const caseId = requireField(ctx.caseId, "petition", "caseId");
      const petitionId = requireField(
        ctx.petitionId,
        "petition",
        "petitionId",
      );
      return `${LX_PREFIX}client:${clientId}:matter:${matterId}:case:${caseId}:pet:${petitionId}`;
    }

    case "document": {
      const clientId = requireField(ctx.clientId, "document", "clientId");
      const matterId = requireField(ctx.matterId, "document", "matterId");
      const caseId = requireField(ctx.caseId, "document", "caseId");
      // AIEditor flow passes docPath (zone-relative path); legacy
      // callers pass documentId. Either is acceptable — the plugin
      // treats the tail string opaquely.
      const tail = ctx.docPath ?? ctx.documentId;
      const docSuffix = requireField(tail, "document", "docPath|documentId");
      return `${LX_PREFIX}client:${clientId}:matter:${matterId}:case:${caseId}:doc:${docSuffix}`;
    }
  }
}

export function parseLxSessionKey(sessionKey: string): LxSessionContext | null {
  if (!sessionKey.startsWith(LX_PREFIX)) return null;
  const rest = sessionKey.slice(LX_PREFIX.length);
  const parts = rest.split(":");

  if (parts[0] === "global") return { type: "global" };
  if (parts[0] === "research" && parts[1]) {
    return { type: "research", topicId: parts[1] };
  }

  // Task D (flat storage): the bare caseId-only case scope `case:{caseId}`. Only
  // the 2-part form is valid — proc/pet/doc leaves stay 4-level (handback), so a
  // `case:{caseId}:…` shape is rejected (the SPA never builds it).
  if (parts[0] === "case") {
    return parts.length === 2 && parts[1] ? { type: "case", caseId: parts[1] } : null;
  }

  // Legacy 4-level nested chain (still emitted for client/matter scopes + parsed for
  // any un-migrated case transcript): client:{c} [ :matter:{m} [ :case:{id} [ :{proc|pet|doc}:{...} ] ] ]
  if (parts[0] !== "client" || !parts[1]) return null;
  const clientId = parts[1];
  if (parts.length === 2) return { type: "client", clientId };

  if (parts[2] !== "matter" || !parts[3]) return null;
  const matterId = parts[3];
  if (parts.length === 4) return { type: "matter", clientId, matterId };

  if (parts[4] !== "case" || !parts[5]) return null;
  const caseId = parts[5];
  if (parts.length === 6) return { type: "case", clientId, matterId, caseId };

  const leafTag = parts[6];
  const leafId = parts.slice(7).join(":");
  if (!leafId) return null;
  if (leafTag === "proc") {
    return { type: "processo", clientId, matterId, caseId, processoId: leafId };
  }
  if (leafTag === "pet") {
    return { type: "petition", clientId, matterId, caseId, petitionId: leafId };
  }
  if (leafTag === "doc") {
    return {
      type: "document",
      clientId,
      matterId,
      caseId,
      docPath: leafId,
    };
  }
  return null;
}

export function isLxSession(sessionKey: string): boolean {
  return sessionKey.startsWith(LX_PREFIX);
}

// DEV-only test accessor so Playwright can exercise round-trip
// `build → parse → build` without importing the module.
declare global {
  interface Window {
    __sessionKey?: {
      build: typeof buildLxSessionKey;
      parse: typeof parseLxSessionKey;
    };
  }
}

if (typeof window !== "undefined" && import.meta.env.DEV) {
  Object.defineProperty(window, "__sessionKey", {
    value: { build: buildLxSessionKey, parse: parseLxSessionKey },
    writable: false,
    enumerable: false,
    configurable: true,
  });
}
